import { formatPrice, formatSubscriptionDateForDisplay } from "./formatters";

export const SCHEDULED_TITLE = "Done";

/**
 * Waiting skips the fee (the one-time vs subscription difference) AND keeps the
 * subscription price on the next delivery, which is that same difference, so the
 * total saving is twice the fee.
 */
function totalSaving(fee: number): number {
  return fee * 2;
}

/** Confirmation title after opting in; carries the same total as the offer. */
export function scheduledTitle(fee: number | null | undefined): string {
  return fee != null && fee > 0
    ? `Done, you saved $${formatPrice(totalSaving(fee))}`
    : SCHEDULED_TITLE;
}

export const KEEP_MY_SUBSCRIPTION = "Keep my subscription";
export const BLOCKED_ACTION_COPY =
  "This subscription ends after its next delivery. Keep your subscription to make this change.";

/** "Nov 4" — short enough for a button or a check line. */
export function shortDeliveryDate(raw: string): string {
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
  if (!ymd) return formatSubscriptionDateForDisplay(raw);
  const date = new Date(Date.UTC(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])));
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

/** The opt-in action: what happens, not what it's worth (the total sits above it). */
export function saveLabel(date: string): string {
  return `Cancel after my ${shortDeliveryDate(date)} delivery`;
}

/** No fee (null or ≤ 0) ⇒ plain "Cancel today" — never "(+$0)". */
export function cancelTodayLabel(amount: number | null | undefined): string {
  return amount != null && amount > 0 ? `Cancel today (+$${formatPrice(amount)})` : "Cancel today";
}

/**
 * After a cancel-flow dialog/chat closes: close the whole sheet only for a real
 * cancel. A successful "cancel after my next delivery" returns to the sheet so
 * the customer sees the scheduled state (banner + Keep my subscription).
 */
export function shouldCloseSheetAfterCancelFlow(
  wasSuccess: boolean,
  cancelAction: string | undefined,
): boolean {
  return wasSuccess && cancelAction !== "cancel_after_next_delivery";
}

export const OR_DIVIDER = "or";

export function waitHeading(date: string): string {
  return `Wait for your ${shortDeliveryDate(date)} delivery`;
}

export function noFeeLine(fee: number): string {
  return `No $${formatPrice(fee)} difference to pay`;
}

/** The subscription discount they still get on the delivery they wait for, framed as a saving. */
export function deliverySavingLine(discount: number, date: string): string {
  return `Save $${formatPrice(discount)} on your ${shortDeliveryDate(date)} delivery`;
}

export const CANCEL_RIGHT_AFTER_LINE = "We cancel right after it ships";

export function totalSavingLine(fee: number): string {
  return `You save $${formatPrice(totalSaving(fee))}`;
}

export function scheduledMessage(date: string): string {
  return `Your last delivery ships on ${formatSubscriptionDateForDisplay(date)} at your subscription price. We'll cancel right after, and you won't be charged again.`;
}

export function bannerTitle(date: string): string {
  return `Ends after your ${formatSubscriptionDateForDisplay(date)} delivery`;
}
